/**
 * User-controlled "hide demo data" toggle. Persists in localStorage and syncs
 * across hook instances/tabs via a custom event (same pattern as theme.ts).
 *
 * When ON, useDemoMode's filterItems drops demo-sourced rows (attributes.source
 * starting with "demo-") even while the demo is loaded — letting the user hide
 * the synthetic onboarding data without removing it, and show it again on demand.
 *
 * Default (when the user has never explicitly chosen): demo is HIDDEN for
 * RETURNING users (onboarding completed) so logging back in lands you on your
 * real data, and SHOWN for first-time users so the onboarding demo still
 * renders. An explicit toggle always wins over this default.
 */
import { useEffect, useState } from 'react';
import { useSession } from '@/features/auth/hooks/useSession';

const STORAGE_KEY = 'xavior:hide-demo';
const CHANGE_EVENT = 'xavior:demo-visibility-changed';

/** Raw per-browser preference: true = hide, false = show, null = never chosen. */
export function getHideDemoPref(): boolean | null {
  try {
    if (typeof window === 'undefined') return null;
    const v = localStorage.getItem(STORAGE_KEY);
    return v === '1' ? true : v === '0' ? false : null;
  } catch {
    return null;
  }
}

export function setHideDemo(hidden: boolean): void {
  try {
    if (typeof window !== 'undefined') {
      // Store the choice explicitly ('0' = show) rather than removing the key,
      // so a user who opts back into the demo isn't re-defaulted to hidden on
      // their next login (see useHideDemo's returning-user default).
      localStorage.setItem(STORAGE_KEY, hidden ? '1' : '0');
    }
  } catch { /* ignore */ }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: hidden }));
  }
}

/**
 * Read + set the EFFECTIVE "hide demo data" state; stays in sync across
 * components/tabs. Falls back to the returning-user default when the user has
 * not explicitly chosen (see file header).
 */
export function useHideDemo(): [boolean, (hidden: boolean) => void] {
  const { profile } = useSession();

  const [pref, setPref] = useState<boolean | null>(getHideDemoPref);

  useEffect(() => {
    const onChange = (e: Event) => setPref(Boolean((e as CustomEvent).detail));
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setPref(getHideDemoPref());
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Explicit choice wins; otherwise default by first-time vs returning user.
  // While the session profile is still loading, default to HIDDEN so a returning
  // user never sees a flash of demo data that then disappears once profile
  // resolves. (A first-timer instead sees the demo appear a beat later — far less
  // jarring than demo vanishing mid-view.)
  const hidden = pref ?? (profile == null ? true : Boolean(profile.onboardingCompleted));
  return [hidden, setHideDemo];
}
